import '@leaflink/stash/styles/backwards-compat.css';
import '@leaflink/stash/styles/base.css';
import '@leaflink/stash/components.css';
import '@/styles/main.scss';

import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { type App as VueApp, createApp } from 'vue';

import { addBasePlugins } from '@/plugins';
import { piniaLocalStoragePlugin } from '@/plugins/pinia/piniaLocalStoragePlugin';
import router from '@/router';
import store from '@/store';
import { unregisterServiceWorker } from '@/utils';
import { initGlobalErrorHandlers } from '@/utils/errors';

import App from './App.vue';
import tanStackQueryClient from './queries/tanStackQueryClient';

/**
 * When hijacking staging for dev mode, the app will already be mounted.
 * Unmount if needed to avoid a warning and intermittent HMR loading issues.
 */
const mountPoint = document.querySelector<Element & { __vue_app__: VueApp }>(`#mp-app`);

if (mountPoint?.__vue_app__) {
  mountPoint.__vue_app__.unmount();
}

/**
 * Root Vue instance, to which all other Components, extensions and plugin-ins
 * are added.
 */
const app = createApp(App);

/**
 * Create the Pinia store
 */
const pinia = createPinia();

pinia.use(piniaPluginPersistedstate);
pinia.use(piniaLocalStoragePlugin);

app.use(pinia);
app.use(VueQueryPlugin, { queryClient: tanStackQueryClient });
app.use(store);
app.use(router);

unregisterServiceWorker();
addBasePlugins(app);
initGlobalErrorHandlers(app);

app.mount(`#mp-app`);
